// Платформо-независимый слой над Telegram и MAX Mini Apps.
//
// Авторизация в обоих мессенджерах устроена одинаково: хост прокидывает
// в страницу мост с подписанной строкой `initData`, которую мы шлём на
// бэкенд. Имя заголовка зависит от платформы: Telegram → `X-Telegram-Init-Data`,
// MAX → `X-Max-Init-Data` (см. getInitDataHeaderName). Этот модуль прячет
// различие «где лежит мост» (window.Telegram vs window.WebApp) и нейминг
// заголовков/ws-параметров, чтобы остальной код не ветвился по платформам.
import { isTelegramMiniApp, getInitData as getTelegramInitData, loadTelegramWebAppScript, } from "./telegram";
import { isMaxMiniApp, getMaxInitData, loadMaxWebAppScript } from "./max";
/** Имена auth-заголовков initData по платформам. */
const INIT_DATA_HEADER = {
    telegram: "X-Telegram-Init-Data",
    max: "X-Max-Init-Data",
};
/** Имена query-параметров initData для WebSocket по платформам. */
const INIT_DATA_WS_PARAM = {
    telegram: "tg_init_data",
    max: "max_init_data",
};
/** Текущая mini-app платформа, либо null (обычный браузер). */
export function getMiniAppPlatform() {
    // Порядок не принципиален: обе проверки идут по СВОЕЙ подписанной initData
    // (мост либо launch-URL), а хост чужого мессенджера её не выдаёт. Telegram
    // первым исторически.
    if (isTelegramMiniApp())
        return "telegram";
    if (isMaxMiniApp())
        return "max";
    return null;
}
/** Запущены ли мы в каком-либо mini-app (Telegram или MAX). */
export function isAnyMiniApp() {
    return getMiniAppPlatform() !== null;
}
/**
 * «Оба моста отработали» = каждый async-скрипт РАЗРЕШИЛСЯ (onload или onerror,
 * флаги ставятся в index.html). Именно «разрешился», а не «создал глобал»:
 * заблокированный/недоступный хост (adblock, DNS-блок, RST) глобал НЕ создаёт,
 * но onerror срабатывает — иначе веб залипал бы на весь таймаут. «Висящее»
 * соединение (MAX-мобилка × telegram.org) не даёт ни того, ни другого, но там
 * платформа распознаётся по initData доступного моста раньше этой проверки.
 */
function bothBridgesSettled() {
    return (typeof window !== "undefined" &&
        window.__maxBridgeSettled === 1 &&
        window.__tgBridgeSettled === 1);
}
/**
 * Дождаться, пока определится mini-app платформа, ПЕРЕД решением «гость vs
 * мини-апп» на старте (см. AuthProvider). Нужно потому, что мост-скрипты в
 * index.html грузятся с `async` (не блокируют бандл — иначе зависший хост вешал
 * бы старт на минуты), и к первому рендеру initData может быть ещё не готов.
 * Синхронный getMiniAppPlatform() в этот момент вернул бы null → уход в гостя
 * («курица-яйцо»). Здесь мы ждём готовность.
 *
 * Возврат:
 * - платформа — как только у неё появился НЕПУСТОЙ initData (fast-path либо в
 *   ходе поллинга; на мобилке это происходит за сотни мс, как только доехал
 *   мост своей платформы — чужой зависший хост не влияет);
 * - null — как только ОБА мост-скрипта разрешились (onload/onerror, флаги в
 *   index.html), а платформа не распозналась: значит мы на обычном вебе — ждать
 *   больше нечего (веб не тормозим лишним таймаутом; onerror ловит и
 *   заблокированный/недоступный хост, который глобал не создаёт);
 * - null по таймауту — фолбэк (например, на вебе хост «висит» без onload/onerror;
 *   ждём не дольше maxWaitTime, а не минуты как раньше на defer).
 */
export async function waitForMiniAppPlatform(maxWaitTime = 3000, checkInterval = 100) {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
        // Детект платформы имеет ПРИОРИТЕТ над коротким замыканием: на десктоп-MAX
        // доступны оба хоста, и window.Telegram.WebApp (пустой) может появиться
        // одновременно с window.WebApp (уже с MAX-initData). Сначала распознаём
        // платформу, только потом делаем вывод «обычный веб».
        const platform = getMiniAppPlatform();
        if (platform)
            return platform;
        // Оба async-моста исполнились, но платформа не распозналась → обычный веб.
        if (bothBridgesSettled())
            return null;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
    return null;
}
/**
 * Контекст мини-аппа для выбора стратегии авторизации (Bearer из localStorage
 * vs cookie на вебе): реальный TG/MAX мини-апп ИЛИ ручной класс `.miniapp` на
 * body/html. ВАЖНО: не по `Boolean(window.WebApp)` — мост MAX подключён
 * глобально в index.html и присутствует на обычном вебе (там initData пуст),
 * так что presence-проверка ловила бы веб как мини-апп и ломала cookie-флоу.
 */
export function isMiniAppContext() {
    if (isAnyMiniApp())
        return true;
    if (typeof document === "undefined")
        return false;
    return (document.body?.classList.contains("miniapp") === true ||
        document.documentElement?.classList.contains("miniapp") === true);
}
/**
 * initData текущей платформы (для initData-заголовка).
 *
 * Обе ветки работают БЕЗ моста: и getTelegramInitData, и getMaxInitData умеют
 * взять подписанную строку из раннего снимка/launch-URL. Это важно: детект
 * платформы здесь гейтит выдачу initData, и пока Telegram определялся только по
 * мосту, подписанная строка из адреса страницы оставалась недостижимой —
 * заголовок X-Telegram-Init-Data не уходил, и бэк видел гостя.
 */
export function getAnyInitData() {
    if (isTelegramMiniApp())
        return getTelegramInitData();
    if (isMaxMiniApp())
        return getMaxInitData();
    return null;
}
/**
 * Имя auth-заголовка initData под текущую платформу:
 * Telegram → `X-Telegram-Init-Data`, MAX → `X-Max-Init-Data`.
 * Вне mini-app (обычный браузер) initData отсутствует, поэтому возвращаем
 * Telegram-имя как безопасный дефолт — фактически заголовок не выставляется.
 */
export function getInitDataHeaderName() {
    return INIT_DATA_HEADER[getMiniAppPlatform() ?? "telegram"];
}
/**
 * Готовый query-параметр initData для URL WebSocket под текущую платформу:
 * Telegram → `tg_init_data=...`, MAX → `max_init_data=...` (значение
 * URL-кодируется). `null`, если данных нет (обычный браузер).
 */
export function getInitDataWsParam() {
    const platform = getMiniAppPlatform();
    if (!platform)
        return null;
    const initData = getAnyInitData();
    if (!initData)
        return null;
    return `${INIT_DATA_WS_PARAM[platform]}=${encodeURIComponent(initData)}`;
}
/**
 * Промис с потолком ожидания. Нужен, потому что loadTelegramWebAppScript /
 * loadMaxWebAppScript на ветке «тег скрипта уже есть в документе» вешают
 * load/error-слушатели на него: если async-скрипт из index.html уже отстрелялся
 * ДО подписки (или «висит» без ответа), промис не разрешится никогда.
 */
function withDeadline(promise, ms) {
    return new Promise((resolve) => {
        const timer = setTimeout(resolve, ms);
        const done = () => {
            clearTimeout(timer);
            resolve();
        };
        void promise.then(done, done);
    });
}
/**
 * Подгрузить мост, если он ещё не инициализирован.
 *
 * Если платформа уже известна — тянем её мост. Если ЕЩЁ НЕ известна, тянем оба
 * недостающих: раньше здесь стоял ранний выход по детекту, и вызов превращался
 * в no-op ровно в том случае, ради которого функция и нужна (мост не доехал →
 * платформа не определилась → мост не подгружаем — замкнутый круг). Мосты,
 * которые уже разрешились сами (флаги из index.html), повторно не ждём.
 * Для обычного браузера это фактически no-op: оба тега уже в документе и
 * settled, а платформы нет.
 */
export async function ensureMiniAppBridge(maxWaitTime = 3000) {
    const platform = getMiniAppPlatform();
    if (platform === "telegram") {
        return withDeadline(loadTelegramWebAppScript(), maxWaitTime);
    }
    if (platform === "max") {
        return withDeadline(loadMaxWebAppScript(), maxWaitTime);
    }
    if (typeof window === "undefined")
        return;
    const pending = [];
    if (!window.Telegram?.WebApp && window.__tgBridgeSettled !== 1) {
        pending.push(loadTelegramWebAppScript());
    }
    if (!window.WebApp && window.__maxBridgeSettled !== 1) {
        pending.push(loadMaxWebAppScript());
    }
    if (pending.length === 0)
        return;
    // Ошибку каждого моста глотаем: чужой/заблокированный хост — норма.
    await withDeadline(Promise.all(pending.map((p) => p.catch(() => undefined))), maxWaitTime);
}
/**
 * Дождаться initData текущей платформы (с таймаутом).
 *
 * Поллим getAnyInitData(), а НЕ ветвимся по платформе однократно: детект сам
 * зависит от initData, поэтому ранний выход при «платформа ещё не определилась»
 * возвращал null мгновенно — ожидание было бессмысленным именно в тот момент,
 * когда оно нужно (мост с `async` ещё в пути). Ранний выход остаётся только для
 * заведомо обычного веба: оба мост-скрипта разрешились, платформы нет.
 */
export async function waitForAnyInitData(maxWaitTime = 3000, checkInterval = 100) {
    const startTime = Date.now();
    for (;;) {
        const initData = getAnyInitData();
        if (initData)
            return initData;
        if (bothBridgesSettled() && !isAnyMiniApp())
            return null;
        if (Date.now() - startTime >= maxWaitTime)
            return null;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
}
